import { useCallback, useEffect, useMemo, useState } from 'react'
import { ServerApiClient } from '../../data/ServerApiClient'
import { CfoDemoLeadGate } from '../cfo-site/CfoDemoLeadGate'
import { TourAuthorRail } from './author/TourAuthorRail'
import { TourLayer, usePortalRoot } from './TourLayer'
import { createPortal } from 'react-dom'
import { useTourNarration } from './useTourNarration'
import { buildTourFacts } from './tourFacts'
import { isDemoSession, setTourGateSuppressed } from '../../data/demoSession'
import { readTourProgress } from './tourSession'
import { TourSelfCheck } from './TourSelfCheck'
import { loadTour } from './tourLoader'
import { readAudienceFromSearch } from './tourTokens'
import { FIRST_LOGIN_TOUR_SLUG, markFirstLoginTourSeen, shouldStartFirstLoginTour } from './firstLoginTour'
import { isTourEnabled, readTourActivation, type TourActivation } from './tourSession'
import { useTourEngine } from './useTourEngine'
import { parseCfoRouteState } from '../parity-dev/cfo/cfoRoutes'
import { flattenTourSteps, type LoadedTour, type TourDoc } from './tourTypes'

/**
 * Mount point for the guided tour.
 *
 * Rendered as an App-level sibling of the demo banner rather than inside
 * `CfoPageShell`'s `overlay` slot, because a tour crosses routes (CEO → cash →
 * forecast) and `App.tsx` swaps the whole CFO surface on `fg_route_change`.
 * Anything mounted inside the shell would unmount mid-tour and lose its state;
 * `CfoDemoBanner` is the working proof that App-level siblings survive.
 *
 * Renders nothing at all unless a `?fg_tour=<slug>` activation is present, so
 * the cost to every non-tour page load is one query-string read.
 */
export function TourHost() {
  const [activation, setActivation] = useState<TourActivation | null>(() => readTourActivation())
  const [tour, setTour] = useState<LoadedTour | null>(null)

  // The activation lives in the query string, which survives in-app navigation
  // via pushState — re-read it whenever the app announces a route change.
  useEffect(() => {
    const controller = new AbortController()
    const sync = () => {
      const next = readTourActivation()
      // In-app navigation rebuilds the URL without our params — clicking the
      // Cash nav item mid-tour strips ?fg_tour, and reading that as "no tour"
      // unmounted the overlay in the middle of a click_anchor step. Adopt new
      // activations; never drop a live one because the URL forgot it.
      if (next) setActivation(next)
    }
    window.addEventListener('fg_route_change', sync, { signal: controller.signal })
    window.addEventListener('popstate', sync, { signal: controller.signal })
    return () => controller.abort()
  }, [])

  // Auto-start on a real customer's first visit to the CFO surface. This is the
  // only path that starts a tour without an explicit `?fg_tour=`, so it is
  // gated hard and marks itself seen immediately.
  useEffect(() => {
    if (activation) return
    const cfoReady = Boolean(parseCfoRouteState(window.location))
    if (!shouldStartFirstLoginTour({ cfoReady })) return
    markFirstLoginTourSeen()
    setActivation({
      slug: FIRST_LOGIN_TOUR_SLUG,
      embed: false,
      embedOrigin: null,
      selfCheck: false,
      author: false,
      variant: null,
    })
  }, [activation])

  const slug = activation?.slug || null

  useEffect(() => {
    if (!slug || !isTourEnabled()) {
      setTour(null)
      return
    }
    let cancelled = false
    void loadTour(slug).then((loaded) => {
      if (!cancelled) setTour(loaded)
    })
    return () => { cancelled = true }
  }, [slug])

  // Authoring edits the loaded document in place, so the engine and the overlay
  // render the draft exactly as a visitor would see it. A preview that is not
  // the runtime is a preview that lies.
  const [authoring, setAuthoring] = useState(false)
  useEffect(() => { setAuthoring(Boolean(activation?.author)) }, [activation?.author])

  const onAuthorChange = useCallback((next: TourDoc) => {
    setTour((prev) => (prev ? { ...prev, doc: next } : prev))
  }, [])

  const portalRoot = usePortalRoot()

  // Resume = they already said yes once; only a fresh visit sees the gate.
  const [started, setStarted] = useState<boolean>(() => {
    try {
      return activation?.slug ? readTourProgress(activation.slug, 0) !== null : false
    } catch {
      return false
    }
  })

  const engine = useTourEngine(tour, activation)

  // While the tour is running, the demo's own module-count ambush stands down:
  // the tour asks for the email at the end, on its own terms.
  useEffect(() => {
    setTourGateSuppressed(engine.active)
    return () => setTourGateSuppressed(false)
  }, [engine.active])

  // Spoken narration: manifest-driven, generated at deploy time. Follows the
  // engine's step, not the other way round — audio ending never advances.
  // Normal signed-in users keep the visual tour but never load or play its
  // audio. Narration belongs to the public demo experience only.
  const narrationAllowed = isDemoSession()
  const narration = useTourNarration(
    engine.active ? tour?.doc.slug ?? null : null,
    narrationAllowed,
  )
  const { onStepChange: narrationOnStepChange } = narration
  const currentStepId = engine.current?.step.id ?? null
  useEffect(() => { narrationOnStepChange(currentStepId) }, [narrationOnStepChange, currentStepId])

  const onPreview = useCallback((stepId: string) => { engine.gotoStep(stepId) }, [engine])

  const api = useMemo(() => new ServerApiClient(), [])

  // The rail writes through founder-gated endpoints, but rendering it for
  // anyone with a query param invites confusion; ask the server who this is.
  const [authorAllowed, setAuthorAllowed] = useState(false)
  useEffect(() => {
    if (!activation?.author) { setAuthorAllowed(false); return }
    let cancelled = false
    void api.getFounderActivityAccess()
      .then((out) => {
        // Authoring is its own capability now: a founder holds it implicitly,
        // a granted teammate holds it explicitly, and everyone else — including
        // someone who can see the CRM — does not get the rail.
        const access = out as { founder?: boolean; capabilities?: string[]; allowed?: boolean }
        const mayAuthor = Boolean(access?.founder) || (access?.capabilities || []).includes('tour_author')
        if (!cancelled) setAuthorAllowed(mayAuthor)
      })
      .catch(() => { if (!cancelled) setAuthorAllowed(false) })
    return () => { cancelled = true }
  }, [activation?.author, api])

  // {{fact:*}} tokens: one cockpit fetch per activation feeds pre-formatted
  // governed numbers into the resolver. Non-blocking — until it lands (or if
  // it never does), fact tokens render their declared defaults.
  const [facts, setFacts] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!engine.active) { setFacts({}); return }
    const route = parseCfoRouteState(window.location)
    if (!route?.workbookId) return
    let cancelled = false
    void api.getQuickBooksCeoCockpit(route.workbookId)
      .then((cockpit) => { if (!cancelled) setFacts(buildTourFacts(cockpit)) })
      .catch(() => { /* defaults carry the copy */ })
    return () => { cancelled = true }
  }, [engine.active, api])

  const tokens = useMemo(() => ({
    audience: readAudienceFromSearch(typeof window === 'undefined' ? '' : window.location.search),
    tokens: tour?.doc.tokens || null,
    facts,
  }), [tour, facts])

  if (!tour) return null
  if (activation?.selfCheck) {
    // Pre-call sanity: no overlay, just the anchor report for this page.
    return <TourSelfCheck doc={tour.doc} />
  }
  if (!engine.active) return null
  // The start gate: the tour announces itself instead of taking over, and the
  // Start click is the user gesture browsers require before audio — so
  // narration begins at step one automatically (unless muted), which no
  // auto-opened overlay can offer. Resumed sessions skip the gate: they
  // already chose to take the tour.
  if (!started && !activation?.author && !activation?.selfCheck) {
    if (!portalRoot) return null
    return createPortal(
      <div className="fg-tour">
        <div className="fg-tour__start" role="dialog" aria-label="Tour available">
          <div className="fg-tour__start-title">{tour.doc.name}</div>
          <div className="fg-tour__start-body">
            {narrationAllowed
              ? 'A narrated two-minute walkthrough — and everything stays clickable while it runs.'
              : 'A two-minute walkthrough — and everything stays clickable while it runs.'}
          </div>
          {narrationAllowed ? (
            <button
              type="button"
              className="fg-tour__start-audio"
              aria-pressed={narration.enabled}
              onClick={() => narration.setNarrationEnabled(!narration.enabled)}
            >
              {narration.enabled ? '🔊 Narration on — tap to mute' : '🔇 Narration off — tap to turn on'}
            </button>
          ) : null}
          <div className="fg-tour__start-actions">
            <button type="button" className="fg-tour__btn fg-tour__btn--link" onClick={() => { setStarted(true); engine.end('declined_start') }}>
              Explore on my own
            </button>
            <button
              type="button"
              className="fg-tour__btn fg-tour__btn--primary"
              onClick={() => {
                setStarted(true)
                // This click is the activation gesture browsers require, so the
                // narration they chose on this card starts immediately —
                // onStepChange is a no-op when they muted it here.
                if (narrationAllowed) narration.onStepChange(engine.current?.step.id ?? null)
              }}
            >
              ▶ Start the tour
            </button>
          </div>
        </div>
      </div>,
      portalRoot,
    )
  }
  return (
    <>
      <TourLayer
        doc={tour.doc}
        engine={engine}
        tokens={tokens}
        narration={narration}
        renderLeadForm={(ref, onCaptured) => (
          <CfoDemoLeadGate
            api={api}
            source="tour"
            // The schema requires title/body on a lead_form widget, but this
            // component received neither — so an author's lead-step copy was
            // silently dead. Pass it through; omitted falls back to the
            // module-gate wording, which is right for the 3-module ambush.
            headline={ref.step.widget.kind === 'lead_form' ? ref.step.widget.title : null}
            subhead={ref.step.widget.kind === 'lead_form' ? ref.step.widget.body : null}
            submitLabel={ref.step.widget.kind === 'lead_form' ? ref.step.widget.submitLabel ?? null : null}
            gatedPage={ref.step.route.page}
            visitedPages={[...new Set(
              flattenTourSteps(tour.doc).slice(0, ref.index).map((r) => r.step.route.page),
            )]}
            onUnlocked={() => onCaptured(null)}
            onBack={() => engine.next()}
          />
        )}
      />
      {authoring && authorAllowed ? (
        <TourAuthorRail
          doc={tour.doc}
          onChange={onAuthorChange}
          onPreview={onPreview}
          onClose={() => setAuthoring(false)}
        />
      ) : null}
    </>
  )
}
